import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/churn-reduction/
// churn-reduction.data-lake-source.ts (CHURN_REDUCTION_DATA_LAKE_CONTEXT_KIND,
// CHURN_REDUCTION_DATA_LAKE_SCHEMA_VERSION).
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 3 APPROVED,
// ingest-anchored rows, latest approvedAt 2026-07-06, asOf 2026-07-05 — 34 days
// against these keys' `monthly` (45-day) ceiling, so they read PRESENT today with
// roughly eleven days of headroom.
//
// One governed snapshot backs all three keys: churn-reduction.data-lake-source.ts
// declares CHURN_REDUCTION_REQUIRED_EVIDENCE_CLASSES and resolves each of them
// from this single artifact, which is what the manifest's three requiredState
// entries name.
export const CHURN_REDUCTION_EVIDENCE_KEYS = {
  churnEvents: 'churn_events',
  cohortRetention: 'cohort_retention',
  savePlayAssumptions: 'save_play_assumptions',
} as const;

type ChurnReductionEvidenceKey =
  (typeof CHURN_REDUCTION_EVIDENCE_KEYS)[keyof typeof CHURN_REDUCTION_EVIDENCE_KEYS];

export const CHURN_REDUCTION_CONTEXT_KINDS = {
  governedSnapshot: 'decision_agent:churn_reduction:governed_snapshot',
} as const;

export const CHURN_REDUCTION_SCHEMA_VERSIONS = {
  governedSnapshot: 'churn_reduction_governed_snapshot_v1',
} as const;

const NO_CHURN_REDUCTION_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors churn-reduction.manifest.ts requiredState exactly (all three share
// sourceClass connector-derived, domain custom, monthly cadence, block).
const churnReductionEntry = (evidenceKey: ChurnReductionEvidenceKey, description: string) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: CHURN_REDUCTION_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: CHURN_REDUCTION_SCHEMA_VERSIONS.governedSnapshot,
    bindingPlaceholders: NO_CHURN_REDUCTION_BINDING_PLACEHOLDERS,
    domain: 'custom',
    freshnessCadence: 'monthly',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description,
  });

export const CHURN_REDUCTION_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'churn-reduction',
  {
    churn_events: churnReductionEntry(
      CHURN_REDUCTION_EVIDENCE_KEYS.churnEvents,
      'Approved customer churn events with revenue at risk, carried by the governed ' +
        'churn-reduction snapshot; the observed loss record every churn read resolves from.',
    ),
    cohort_retention: churnReductionEntry(
      CHURN_REDUCTION_EVIDENCE_KEYS.cohortRetention,
      'Approved cohort retention curves carried by the same governed snapshot; separates ' +
        'genuine retention decay from mix change when churn is decomposed.',
    ),
    save_play_assumptions: churnReductionEntry(
      CHURN_REDUCTION_EVIDENCE_KEYS.savePlayAssumptions,
      'Approved save-play assumptions carried by the same governed snapshot; bounds what a ' +
        'retention intervention may claim rather than letting the scenario invent a save rate.',
    ),
  },
  {
    evidenceKeys: Object.values(CHURN_REDUCTION_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: Object.values(CHURN_REDUCTION_CONTEXT_KINDS),
    schemaVersions: Object.values(CHURN_REDUCTION_SCHEMA_VERSIONS),
    stateKeys: Object.values(CHURN_REDUCTION_EVIDENCE_KEYS),
    bindingPlaceholders: NO_CHURN_REDUCTION_BINDING_PLACEHOLDERS,
  },
);

export type ChurnReductionModuleEvidenceContract = typeof CHURN_REDUCTION_MODULE_EVIDENCE_CONTRACT;
